import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, Palette, Link as LinkIcon, Image as ImageIcon, X, Sparkles, Wand2, Lightbulb, Loader2 } from 'lucide-react';
import { RESUME_TEMPLATES, SENIORITY_LEVELS, INDUSTRIES, HIGHLIGHT_COLORS } from '../config/constants';
import { COMMON_SKILLS, getMatchingSkills } from '../config/skillsDatabase';
import resumeAPI from '../services/api';
import { rewriteExperienceBullet, suggestSkills } from '../services/aiService';
import LinkedInImport from './LinkedInImport';
import ProfileImport from './ProfileImport';
import EducationImport from './EducationImport';
import CertificationsImport from './CertificationsImport';

// Move Section component outside to prevent recreation on each render
const Section = React.memo(({ title, name, expanded, onToggle, hasData, children }) => (
  <div className={`rounded-lg shadow-md mb-4 ${!expanded && hasData ? 'bg-blue-50 border-l-4 border-primary-500' : 'bg-white'}`}>
    <button
      onClick={() => onToggle(name)}
      className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-lg"
    >
      <div className="flex items-center gap-2">
        <h3 className={`text-lg font-semibold ${!expanded && hasData ? 'text-primary-700' : 'text-gray-900'}`}>{title}</h3>
        {!expanded && hasData && (
          <span className="inline-block w-2 h-2 rounded-full bg-primary-500" title="Contains information" />
        )}
      </div>
      {expanded ? (
        <ChevronUp className="w-5 h-5 text-gray-600" />
      ) : (
        <ChevronDown className={`w-5 h-5 ${hasData ? 'text-primary-500' : 'text-gray-600'}`} />
      )}
    </button>
    {expanded && <div className="px-6 pb-6">{children}</div>}
  </div>
));

Section.displayName = 'Section';

const ResumeEditor = ({ resume, updateResume, hideAbout = false }) => {
  const formatTemplateLabel = (value) => {
    const raw = String(value || '');
    const cleaned = raw.replace(/^json-/, '').replace(/-/g, ' ').trim();
    const title = cleaned
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return raw.startsWith('json-') ? `${title} (JSON)` : title;
  };

  const [expandedSections, setExpandedSections] = useState({
    basic: false,
    contact: false,
    professional: false,
    experience: false,
    education: false,
    training: false,
    skills: false,
    projects: false,
    certifications: false,
    languages: false
  });

  // Collapse saved training entries by default on load (run only once)
  const initialTrainingCollapseSet = useRef(false);
  useEffect(() => {
    if (initialTrainingCollapseSet.current) return; // Already applied — don't re-collapse on every keystroke
    if (resume.training && resume.training.length > 0) {
      const initialCollapsed = {};
      resume.training.forEach((training, index) => {
        // Check if training has data inline to avoid dependency on hasTrainingData
        const hasData = !!(training.name || training.provider || training.location || 
                          training.start_date || training.end_date || training.description || training.certificate_url);
        if (hasData) {
          initialCollapsed[index] = true; // Collapse entries with data
        }
      });
      if (Object.keys(initialCollapsed).length > 0) {
        setCollapsedTraining(initialCollapsed);
        initialTrainingCollapseSet.current = true;
      }
    }
  }, [resume.training]);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);
  
  // Section-level "has data" flags for highlight indicators
  const sectionHasData = useMemo(() => ({
    basic: !!(resume.title || resume.headline || resume.credentials || resume.summary || (resume.core_skills && resume.core_skills.length > 0) || resume.photo_url || resume.banner_url),
    contact: !!(resume.contact_email || resume.contact_phone || resume.linkedin_url || resume.contact_location || resume.website_url),
    professional: !!(resume.target_role || resume.target_industry || resume.seniority_level || resume.highlight_color),
    experience: (resume.experience || []).some(exp => !!(exp.title || exp.company || exp.description)),
    education: (resume.education || []).some(edu => !!(edu.degree || edu.institution || edu.field_of_study)),
    training: (resume.training || []).some(t => !!(t.name || t.provider || t.description)),
    skills: (resume.skills || []).length > 0,
    certifications: (resume.certifications || []).some(c => !!(c.name || c.issuer)),
    languages: (resume.languages || []).some(l => !!(l.name)),
  }), [resume]);

  // Media modal state
  const [mediaModalOpen, setMediaModalOpen] = useState({ expIndex: null, mediaIndex: null });
  
  // Skills autocomplete state - key format: `${expIndex}-${skillIndex}`
  const [skillsInputState, setSkillsInputState] = useState({});
  
  // Auto-fill text state for each experience entry
  const [autoFillText, setAutoFillText] = useState({});
  
  // Collapsed items state - track which entries are collapsed for each section
  const [collapsedExperiences, setCollapsedExperiences] = useState({});
  const [collapsedEducation, setCollapsedEducation] = useState({});
  const [collapsedTraining, setCollapsedTraining] = useState({});
  const [collapsedProjects, setCollapsedProjects] = useState({});
  const [collapsedCertifications, setCollapsedCertifications] = useState({});
  const [collapsedLanguages, setCollapsedLanguages] = useState({});
  
  // AI state
  const [aiRewritingIndex, setAiRewritingIndex] = useState(null); // index of experience being rewritten
  const [aiRewriteResult, setAiRewriteResult] = useState({}); // { index: 'rewritten text' }
  const [aiSuggestingSkills, setAiSuggestingSkills] = useState(false);
  const [aiSuggestedSkills, setAiSuggestedSkills] = useState([]);
  const [aiError, setAiError] = useState('');

  // Get user's skills from resume
  const userSkills = useMemo(() => resume.skills || [], [resume.skills]);
  
  // Helper functions to check if items have data (to determine if they should be collapsible)
  const hasExperienceData = useCallback((exp) => {
    return !!(exp.title || exp.company || exp.start_month || exp.start_year || 
              exp.end_month || exp.end_year || exp.location || exp.description || 
              (exp.skills && exp.skills.length > 0) || (exp.media && exp.media.length > 0) ||
              (exp.achievements && exp.achievements.length > 0));
  }, []);
  
  const hasEducationData = useCallback((edu) => {
    return !!(edu.degree || edu.field_of_study || edu.institution || edu.start_month || edu.start_year ||
              edu.end_month || edu.end_year || edu.location || edu.gpa || edu.description || edu.activities_and_societies);
  }, []);
  
  const hasTrainingData = useCallback((training) => {
    return !!(training.name || training.provider || training.location || 
              training.start_date || training.end_date || training.description || training.certificate_url);
  }, []);
  
  const hasProjectData = useCallback((project) => {
    return !!(project.name || project.url || project.description || 
              (project.technologies && project.technologies.length > 0));
  }, []);
  
  const hasCertificationData = useCallback((cert) => {
    return !!(cert.name || cert.issuer || cert.issue_month || cert.issue_year || cert.expiration_month || cert.expiration_year || cert.credential_id || cert.credential_url || cert.certification_logo || (cert.skills && cert.skills.length > 0) || cert.date || cert.url);
  }, []);
  
  const hasLanguageData = useCallback((lang) => {
    return !!(lang.name || lang.proficiency);
  }, []);
  
  // Toggle collapse state functions for each section
  const toggleExperienceCollapse = useCallback((index) => {
    setCollapsedExperiences(prev => ({ ...prev, [index]: !prev[index] }));
  }, []);
  
  const toggleEducationCollapse = useCallback((index) => {
    setCollapsedEducation(prev => ({ ...prev, [index]: !prev[index] }));
  }, []);
  
  const toggleTrainingCollapse = useCallback((index) => {
    setCollapsedTraining(prev => ({ ...prev, [index]: !prev[index] }));
  }, []);
  
  const toggleProjectCollapse = useCallback((index) => {
    setCollapsedProjects(prev => ({ ...prev, [index]: !prev[index] }));
  }, []);
  
  const toggleCertificationCollapse = useCallback((index) => {
    setCollapsedCertifications(prev => ({ ...prev, [index]: !prev[index] }));
  }, []);
  
  const toggleLanguageCollapse = useCallback((index) => {
    setCollapsedLanguages(prev => ({ ...prev, [index]: !prev[index] }));
  }, []);

  const addItem = useCallback((field) => {
    const newItem = getDefaultItem(field);
    const currentItems = resume[field];
    const items = Array.isArray(currentItems) ? [...currentItems] : [];
    updateResume(field, [...items, newItem]);
  }, [resume, updateResume]);

  const updateItem = useCallback((field, index, key, value) => {
    // Ensure the field exists and is an array
    const currentItems = resume[field];
    const items = Array.isArray(currentItems) ? [...currentItems] : [];
    
    // Ensure the index exists
    if (!items[index]) {
      items[index] = {};
    }
    
    // Update the item
    items[index] = { ...items[index], [key]: value };
    
    // Update the resume
    updateResume(field, items);
  }, [resume, updateResume]);

  const removeItem = useCallback((field, index) => {
    const items = [...(resume[field] || [])];
    items.splice(index, 1);
    updateResume(field, items);
  }, [resume, updateResume]);

  // AI: Rewrite experience bullet
  const handleAiRewrite = useCallback(async (index) => {
    const exp = (resume.experience || [])[index];
    if (!exp?.description) return;
    setAiRewritingIndex(index);
    setAiError('');
    try {
      const result = await rewriteExperienceBullet(exp.description, exp.title || '');
      setAiRewriteResult(prev => ({ ...prev, [index]: result }));
    } catch (err) {
      setAiError(err.message || 'AI rewrite failed');
    } finally {
      setAiRewritingIndex(null);
    }
  }, [resume.experience]);

  const handleAcceptRewrite = useCallback((index) => {
    const rewritten = aiRewriteResult[index];
    if (rewritten) {
      updateItem('experience', index, 'description', rewritten);
      setAiRewriteResult(prev => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  }, [aiRewriteResult, updateItem]);

  const handleDismissRewrite = useCallback((index) => {
    setAiRewriteResult(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }, []);

  // AI: Suggest skills
  const handleAiSuggestSkills = useCallback(async () => {
    setAiSuggestingSkills(true);
    setAiError('');
    setAiSuggestedSkills([]);
    try {
      const suggestions = await suggestSkills(resume);
      setAiSuggestedSkills(suggestions);
    } catch (err) {
      setAiError(err.message || 'AI skill suggestion failed');
    } finally {
      setAiSuggestingSkills(false);
    }
  }, [resume]);

  const handleAddSuggestedSkill = useCallback((skill) => {
    const current = resume.skills || [];
    if (!current.includes(skill)) {
      updateResume('skills', [...current, skill]);
    }
    setAiSuggestedSkills(prev => prev.filter(s => s !== skill));
  }, [resume.skills, updateResume]);
  
  // Function to parse text and auto-fill experience fields
  const parseAndAutoFill = useCallback((text, expIndex) => {
    if (!text || !text.trim()) {
      console.warn('Auto-fill: Empty text provided');
      return;
    }
    
    console.log('Auto-fill: Parsing text:', text.substring(0, 200));
    
    const parsed = {
      title: '',
      company: '',
      start_month: '',
      start_year: '',
      end_month: '',
      end_year: '',
      is_current: false,
      location: '',
      location_type: 'On-site', // Default to On-site
      employment_type: 'Full-time', // Default to Full-time when using auto-fill
      description: '',
      skills: []
    };
    
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    const fullText = text.toLowerCase();
    
    // LinkedIn format: Company -> Employment type · duration -> Location type -> Title -> Dates · duration -> Location -> Description -> Skills
    // Step 1: Identify LinkedIn format markers
    let employmentTypeIndex = -1;
    let locationTypeIndex = -1;
    let dateIndex = -1;
    let locationIndex = -1;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const lowerLine = line.toLowerCase();
      
      // Employment type line
      if ((lowerLine.includes('full-time') || lowerLine.includes('part-time') || 
           lowerLine.includes('contract') || lowerLine.includes('internship')) &&
          (lowerLine.includes('yrs') || lowerLine.includes('mos') || lowerLine.includes('·'))) {
        employmentTypeIndex = i;
        if (lowerLine.includes('full-time')) parsed.employment_type = 'Full-time';
        else if (lowerLine.includes('part-time')) parsed.employment_type = 'Part-time';
        else if (lowerLine.includes('contract')) parsed.employment_type = 'Contract';
        else if (lowerLine.includes('internship')) parsed.employment_type = 'Internship';
      }
      
      // Location type (allow duplicates like "On-siteOn-site")
      if (/^on-site(\s*on-site)*$/i.test(lowerLine.trim()) || 
          /^remote(\s*remote)*$/i.test(lowerLine.trim()) || 
          /^hybrid(\s*hybrid)*$/i.test(lowerLine.trim())) {
        if (locationTypeIndex === -1) locationTypeIndex = i;
        parsed.location_type = 'On-site';
      }
      
      // Date line
      if (/\w+\s+\d{4}\s*[-–—]\s*(\w+\s+\d{4}|Present|Current)/i.test(line) || 
          /\d{4}\s*[-–—]\s*(\d{4}|Present|Current)/.test(line)) {
        if (dateIndex === -1) dateIndex = i;
      }
      
      // Location (after dates, short format like "JBLM, WA")
      // Also handle cases where location might have duplicates on the same line
      if (dateIndex >= 0 && i > dateIndex) {
        const cleanLine = line.trim();
        // Match short location codes (2-5 letters, comma, 2-letter state)
        const locationMatch = cleanLine.match(/^([A-Z]{2,5},\s*[A-Z]{2})(?:\s+\1)*$/);
        if (locationMatch) {
          locationIndex = i;
          parsed.location = locationMatch[1]; // Take first occurrence
          console.log('Auto-fill: Found location:', parsed.location, 'at line index', i);
        }
      }
    }
    
    // Step 2: Extract company (first line, before employment type)
    if (employmentTypeIndex > 0 && !parsed.company) {
      // Take the first line that's not a title-looking line
      for (let i = 0; i < employmentTypeIndex; i++) {
        const line = lines[i];
        // Company should not look like a job title (no "Director", "Manager", etc. at start)
        // and should not be too long (titles are usually longer)
        if (line.length > 2 && line.length < 80 && 
            !line.match(/^(Full-time|Part-time|Contract|Internship|On-site|Remote|Hybrid)/i) &&
            !line.match(/^(Director|Manager|Engineer|Analyst|Specialist|Lead|Senior|Junior|Coordinator|Officer|President|VP|Chief)/i)) {
          parsed.company = line;
          break;
        }
      }
    }
    
    // Step 3: Extract title (after location type or employment type, before dates)
    // Try: between location type and dates first, then between employment type and dates
    const titleSearchStart = locationTypeIndex >= 0 ? locationTypeIndex + 1 : employmentTypeIndex >= 0 ? employmentTypeIndex + 1 : 0;
    const titleSearchEnd = dateIndex >= 0 ? dateIndex : lines.length;
    
    if (titleSearchEnd > titleSearchStart && !parsed.title) {
      for (let i = titleSearchStart; i < titleSearchEnd; i++) {
        const line = lines[i].trim();
        // Skip location type, employment-like, or location-code lines
        if (/^(On-site|Remote|Hybrid|Full-time|Part-time|Contract|Internship)(\s*\1)*$/i.test(line) ||
            /^[A-Z]{2,5},\s*[A-Z]{2}(\s+\1)*$/.test(line)) continue;
        
        // Remove any trailing duration info (like "· 1 yr 7 mos")
        const cleanLine = line.replace(/\s*·\s*\d+\s*(yr|mos).*$/i, '').trim();
        if (cleanLine.length < 10) continue;
        
        // Accept if it looks like a title: has title keywords OR 3+ words, and not company-like
        const hasTitleKeyword = /(Director|Manager|Engineer|Analyst|Specialist|Lead|Senior|Junior|Coordinator|Officer|President|VP|Chief|Aviation|Maintenance|Readiness|Ground)/i.test(cleanLine);
        const hasEnoughWords = cleanLine.split(/\s+/).length >= 3;
        const notCompany = !/^\d+(st|nd|rd|th)\s+Infantry\s+Division$/i.test(cleanLine) && 
                           !/^\d+(st|nd|rd|th)\s+[A-Za-z]+\s+(Division|Brigade|Regiment)$/i.test(cleanLine);
        
        if (cleanLine.length <= 150 && (hasTitleKeyword || hasEnoughWords) && notCompany) {
          parsed.title = cleanLine;
          break;
        }
      }
    }
    
    // Fallback: Traditional format (only if LinkedIn format didn't work)
    // Don't use fallback if we already have a company (means LinkedIn format was detected)
    if (!parsed.title && !parsed.company && lines.length >= 2) {
      // Traditional: Title first, Company second
      if (lines[0].length > 10 && lines[0].length < 150 && 
          !lines[0].match(/^(Full-time|Part-time|Contract|Internship|Volunteer|Freelance)/i) &&
          (lines[0].match(/(Director|Manager|Engineer|Analyst|Specialist|Lead|Senior|Junior|Coordinator|Officer)/i) ||
           lines[0].split(' ').length >= 3)) {
        parsed.title = lines[0];
      }
      if (!parsed.title && lines[1].length > 2 && lines[1].length < 100 && 
          !lines[1].match(/^(Full-time|Part-time|Contract|Internship|Volunteer|Freelance)/i)) {
        parsed.company = lines[1];
      }
    }
    
    // Parse dates - look for patterns like "Jan 2020 - Feb 2024" or "2020 - 2024" or "Jul 2002 - Feb 2024"
    const datePatterns = [
      /(\w+)\s+(\d{4})\s*[-–—]\s*(\w+)\s+(\d{4})/i, // "Jan 2020 - Feb 2024"
      /(\w+)\s+(\d{4})\s*[-–—]\s*(Present|Current)/i, // "Jan 2020 - Present"
      /(\d{4})\s*[-–—]\s*(\d{4})/, // "2020 - 2024"
      /(\d{4})\s*[-–—]\s*(Present|Current)/i, // "2020 - Present"
      /(\d{1,2})\/(\d{4})\s*[-–—]\s*(\d{1,2})\/(\d{4})/, // "1/2020 - 2/2024"
      /(\d{1,2})\/(\d{4})\s*[-–—]\s*(Present|Current)/i // "1/2020 - Present"
    ];
    
    for (const pattern of datePatterns) {
      const match = text.match(pattern);
      if (match) {
        const monthMap = {
          'jan': 'Jan', 'january': 'Jan',
          'feb': 'Feb', 'february': 'Feb',
          'mar': 'Mar', 'march': 'Mar',
          'apr': 'Apr', 'april': 'Apr',
          'may': 'May',
          'jun': 'Jun', 'june': 'Jun',
          'jul': 'Jul', 'july': 'Jul',
          'aug': 'Aug', 'august': 'Aug',
          'sep': 'Sep', 'september': 'Sep',
          'oct': 'Oct', 'october': 'Oct',
          'nov': 'Nov', 'november': 'Nov',
          'dec': 'Dec', 'december': 'Dec'
        };
        
        if (pattern.source.includes('\\w+')) {
          // Month name format
          parsed.start_month = monthMap[match[1].toLowerCase()] || match[1].substring(0, 3);
          parsed.start_year = match[2];
          if (match[3] && match[3].toLowerCase() !== 'present' && match[3].toLowerCase() !== 'current') {
            parsed.end_month = monthMap[match[3].toLowerCase()] || match[3].substring(0, 3);
            parsed.end_year = match[4];
          } else {
            parsed.is_current = true;
          }
        } else if (pattern.source.includes('\\d{1,2}')) {
          // MM/YYYY format
          const monthNum = parseInt(match[1]);
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          parsed.start_month = monthNames[monthNum - 1] || '';
          parsed.start_year = match[2];
          if (match[3] && match[3].toLowerCase() !== 'present' && match[3].toLowerCase() !== 'current') {
            const endMonthNum = parseInt(match[3]);
            parsed.end_month = monthNames[endMonthNum - 1] || '';
            parsed.end_year = match[4];
          } else {
            parsed.is_current = true;
          }
        } else {
          // Year only format
          parsed.start_year = match[1];
          if (match[2] && match[2].toLowerCase() !== 'present' && match[2].toLowerCase() !== 'current') {
            parsed.end_year = match[2];
          } else {
            parsed.is_current = true;
          }
        }
        break;
      }
    }
    
    // Title extraction fallback - run whenever title is still empty
    if (!parsed.title) {
      // Look for title patterns that contain job title keywords (e.g. "Director of Aviation and Ground Maintenance Readiness")
      const titlePatterns = [
        /\b(Director\s+of\s+[A-Za-z\s&,().-]+(?:Readiness|Maintenance|Aviation|Ground|Operations|Engineering)?)\b/i,
        /\b(Manager\s+of\s+[A-Za-z\s&,().-]+)\b/i,
        /\b(Senior\s+[A-Za-z\s&,().-]+(?:Engineer|Analyst|Developer|Manager|Specialist))\b/i,
        /\b([A-Za-z\s&,().-]+(?:Director|Manager|Engineer|Analyst|Specialist|Lead|Officer|Coordinator|Consultant|Advisor|Executive|President|VP|Chief))(?:\s+at|\s+@|[\n·]|$)/i
      ];
      
      for (const pattern of titlePatterns) {
        const match = text.match(pattern);
        if (match && match[1]) {
          const candidate = match[1].trim();
          // Must be title-like: not company, not location, reasonable length
          if (candidate.length > 15 && candidate.length < 150 &&
              !candidate.toLowerCase().includes('infantry') &&
              !candidate.toLowerCase().includes('division') &&
              !/^[A-Z]{2,5},\s*[A-Z]{2}$/.test(candidate) &&
              !/^(Full-time|Part-time|On-site|Remote|Hybrid)/i.test(candidate)) {
            parsed.title = candidate;
            break;
          }
        }
      }
    }
    
    // Extract company - look for "at Company" or "Company" patterns (only if not already found)
    if (!parsed.company) {
      const companyPatterns = [
        /(?:at|@)\s+([A-Z][A-Za-z0-9\s&,.-]+(?:Inc|LLC|Corp|Company|Ltd|Systems|Solutions|Technologies|Group|Associates|Army|Navy|Air Force|Marines|US Army|U\.?S\.?\s*Army)?)/i,
        /^([A-Z][A-Za-z0-9\s&,.-]+(?:Inc|LLC|Corp|Company|Ltd|Systems|Solutions|Technologies|Group|Associates|Army|Navy|Air Force|Marines|US Army|U\.?S\.?\s*Army)?)$/m,
        /(US\s+Army|U\.?S\.?\s+Army)/i
      ];
      
      for (const pattern of companyPatterns) {
        const match = text.match(pattern);
        if (match && match[1]) {
          parsed.company = match[1].trim();
          break;
        }
      }
    }
    
    // Location extraction is now handled above in the LinkedIn format detection section
    // This fallback is only if location wasn't found in LinkedIn format
    
    // Extract employment type
    if (fullText.includes('full-time') || fullText.includes('full time')) {
      parsed.employment_type = 'Full-time';
    } else if (fullText.includes('part-time') || fullText.includes('part time')) {
      parsed.employment_type = 'Part-time';
    } else if (fullText.includes('contract')) {
      parsed.employment_type = 'Contract';
    } else if (fullText.includes('internship')) {
      parsed.employment_type = 'Internship';
    } else if (fullText.includes('volunteer')) {
      parsed.employment_type = 'Volunteer';
    } else if (fullText.includes('freelance')) {
      parsed.employment_type = 'Freelance';
    }
    
    // Extract skills - look for "Skills:" section at the end
    const skillsMatch = text.match(/Skills:\s*([^\n]+(?:\n[^\n]+)*)/i);
    if (skillsMatch) {
      // Parse skills from the Skills: line (usually separated by · or commas)
      const skillsText = skillsMatch[1];
      const skillsList = skillsText.split(/[·,•]/).map(s => s.trim()).filter(s => s.length > 0);
      parsed.skills = skillsList.slice(0, 20); // Limit to top 20 skills
    } else {
      // Fallback: Extract skills from text by matching against COMMON_SKILLS
      const foundSkills = [];
      for (const skill of COMMON_SKILLS) {
        const skillRegex = new RegExp(`\\b${skill.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        if (skillRegex.test(text)) {
          foundSkills.push(skill);
        }
      }
      parsed.skills = foundSkills.slice(0, 10); // Limit to top 10 skills
    }
    
    // Extract description - preserve paragraph structure, remove all metadata
    // Find where description starts (after location or dates) and ends (before Skills:)
    let descriptionStart = -1;
    let descriptionEnd = text.length;
    
    // Find end: before "Skills:" section
    const skillsMatchForDesc = text.match(/Skills:/i);
    if (skillsMatchForDesc) {
      descriptionEnd = skillsMatchForDesc.index;
    }
    
    // Find start: look for location line, then dates line, use whichever comes last
    // In LinkedIn format: dates come before location, so description starts after location
    if (locationIndex >= 0 && parsed.location) {
      // Find the location line in the original text (with newlines)
      const locationLine = lines[locationIndex];
      const locationPos = text.indexOf(locationLine);
      if (locationPos >= 0) {
        descriptionStart = locationPos + locationLine.length;
        // Skip to next line
        const nextNewline = text.indexOf('\n', descriptionStart);
        if (nextNewline >= 0) {
          descriptionStart = nextNewline + 1;
        }
      }
    } else if (dateIndex >= 0) {
      // Fallback: if no location found, start after dates
      const dateLine = lines[dateIndex];
      const datePos = text.indexOf(dateLine);
      if (datePos >= 0) {
        descriptionStart = datePos + dateLine.length;
        // Skip to next line
        const nextNewline = text.indexOf('\n', descriptionStart);
        if (nextNewline >= 0) descriptionStart = nextNewline + 1;
      }
    }
    
    // Extract description section - preserve original paragraph spacing
    let description = descriptionStart >= 0 
      ? text.substring(descriptionStart, descriptionEnd)
      : text.substring(0, descriptionEnd);
    
    // Remove Skills section if still present
    description = description.replace(/Skills:.*$/is, '');
    
    // Remove duplicate entries of title, company, location - preserve paragraph breaks
    if (parsed.title) {
      const titleEscaped = parsed.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Split by newlines, process each line, but preserve empty lines (paragraph breaks)
      description = description.split('\n').map(line => {
        const cleaned = line.replace(new RegExp(titleEscaped, 'gi'), '').trim();
        // Preserve empty lines (they represent paragraph breaks)
        return cleaned.length === 0 && line.trim().length === 0 ? '' : cleaned;
      }).join('\n');
    }
    
    if (parsed.company) {
      const companyEscaped = parsed.company.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      description = description.split('\n').map(line => {
        const cleaned = line.replace(new RegExp(companyEscaped, 'gi'), '').trim();
        return cleaned.length === 0 && line.trim().length === 0 ? '' : cleaned;
      }).join('\n');
    }
    
    if (parsed.location) {
      const locationEscaped = parsed.location.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      description = description.split('\n').map(line => {
        const cleaned = line.replace(new RegExp(locationEscaped, 'gi'), '').trim();
        return cleaned.length === 0 && line.trim().length === 0 ? '' : cleaned;
      }).join('\n');
    }
    
    // Remove metadata lines (employment type, location type, dates with duration) - preserve paragraph breaks
    description = description.split('\n')
      .map(line => {
        const trimmed = line.trim();
        // Remove lines that are just metadata, but preserve empty lines
        if (trimmed.length === 0) return ''; // Preserve empty lines (paragraph breaks)
        if (trimmed.match(/^(Full-time|Part-time|Contract|Internship|On-site|Remote|Hybrid)$/i)) return '';
        if (trimmed.match(/\d+\s*(yrs|mos)/i) && trimmed.length < 30) return '';
        if (trimmed.match(/^\w+\s+\d{4}\s*[-–—]\s*(\w+\s+\d{4}|Present|Current).*?(yrs|mos|·)/i)) return '';
        return line;
      })
      .join('\n'); // Join with newlines to preserve paragraph structure
    
    // Clean up: remove duplicate words/phrases at start, preserve paragraph breaks
    description = description
      .replace(/^(\w+\s+){1,3}\1+/gm, '$1') // Remove duplicate short phrases at start of lines
      .replace(/\s{3,}/g, '\n\n') // Multiple spaces become paragraph breaks
      .replace(/\n{4,}/g, '\n\n\n') // More than 3 newlines become triple (but keep some spacing)
      .trim();
    
    // Ensure bullet points are preserved with proper spacing
    description = description.replace(/\n([•·])\s*/g, '\n\n$1 '); // Add space after bullet
    
    if (description.length > 2000) {
      description = description.substring(0, 2000);
      // Try to cut at a sentence boundary
      const lastPeriod = description.lastIndexOf('.');
      if (lastPeriod > 1800) {
        description = description.substring(0, lastPeriod + 1);
      }
    }
    
    parsed.description = description;
    
    // Log what was parsed
    console.log('Auto-fill: Parsed data:', parsed);
    
    // Apply parsed data to the experience entry - batch all updates into one
    try {
      const currentExperiences = [...(resume.experience || [])];
      
      // Ensure the array is long enough
      while (currentExperiences.length <= expIndex) {
        currentExperiences.push({});
      }
      
      const currentExp = currentExperiences[expIndex] || {};
      
      // Build updated experience object with all parsed values
      const updatedExp = { ...currentExp };
      
      Object.keys(parsed).forEach(key => {
        const value = parsed[key];
        // Update all meaningful values, including false for is_current and empty strings for location_type default
        if (value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            // Always set arrays, even if empty (to clear previous values)
            updatedExp[key] = value;
          } else if (key === 'is_current') {
            // Always set is_current, even if false
            updatedExp[key] = value;
          } else if (key === 'location_type' && value === 'On-site') {
            // Always set location_type default
            updatedExp[key] = value;
          } else if (key === 'employment_type' && value === 'Full-time') {
            // Always set employment_type default
            updatedExp[key] = value;
          } else if (value !== '') {
            // For other fields, only set non-empty strings
            updatedExp[key] = value;
          }
        }
      });
      
      // Update the entire experience entry at once
      currentExperiences[expIndex] = updatedExp;
      
      console.log('Auto-fill: Updating experience at index', expIndex, 'with:', updatedExp);
      console.log('Auto-fill: Full experiences array:', currentExperiences);
      console.log('Auto-fill: Current resume.experience before update:', resume.experience);
      
      // Create a new array reference to ensure React detects the change
      const newExperiences = currentExperiences.map((exp, idx) => 
        idx === expIndex ? updatedExp : exp
      );
      
      updateResume('experience', newExperiences);
      
      console.log('Auto-fill: Successfully applied parsed data', updatedExp);
      console.log('Auto-fill: New experiences array sent to updateResume:', newExperiences);
      
      // Show success message
      const filledFields = Object.keys(parsed).filter(key => {
        const value = parsed[key];
        return value !== '' && value !== false && value !== null && value !== undefined && 
               (!Array.isArray(value) || value.length > 0);
      });
      if (filledFields.length > 0) {
        alert(`Auto-filled ${filledFields.length} field(s): ${filledFields.join(', ')}`);
      }
    } catch (error) {
      console.error('Auto-fill: Error applying parsed data:', error);
      alert('Error auto-filling fields. Please check the console for details.');
    }
    
    // Clear the auto-fill text
    setAutoFillText(prev => {
      const updated = { ...prev };
      delete updated[expIndex];
      return updated;
    });
  }, [resume, updateResume]);

  const handleLinkedInImport = useCallback((importedExperiences) => {
    // Transform imported experiences to match the expected format
    // Each experience from LinkedIn becomes a separate work experience entry
    const transformedExperiences = importedExperiences.map(exp => {
      // Helper function to parse dates from strings like "Jan 2020" or "2020" into month and year
      const parseDate = (dateStr) => {
        if (!dateStr || dateStr === 'Present' || dateStr === 'Current') {
          return { month: '', year: '' };
        }
        
        // Try to parse "Jan 2020" or "January 2020" format
        const monthYearMatch = dateStr.match(/(Jan|January|Feb|February|Mar|March|Apr|April|May|Jun|June|Jul|July|Aug|August|Sep|September|Oct|October|Nov|November|Dec|December)\s+(\d{4})/i);
        if (monthYearMatch) {
          const monthMap = {
            'jan': 'Jan', 'january': 'Jan',
            'feb': 'Feb', 'february': 'Feb',
            'mar': 'Mar', 'march': 'Mar',
            'apr': 'Apr', 'april': 'Apr',
            'may': 'May',
            'jun': 'Jun', 'june': 'Jun',
            'jul': 'Jul', 'july': 'Jul',
            'aug': 'Aug', 'august': 'Aug',
            'sep': 'Sep', 'september': 'Sep',
            'oct': 'Oct', 'october': 'Oct',
            'nov': 'Nov', 'november': 'Nov',
            'dec': 'Dec', 'december': 'Dec'
          };
          const month = monthMap[monthYearMatch[1].toLowerCase()] || monthYearMatch[1].substring(0, 3);
          return { month, year: monthYearMatch[2] };
        }
        
        // Try to parse "MM/YYYY" format
        const slashMatch = dateStr.match(/(\d{1,2})\/(\d{4})/);
        if (slashMatch) {
          const monthNum = parseInt(slashMatch[1]);
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return { month: monthNames[monthNum - 1] || '', year: slashMatch[2] };
        }
        
        // Try to parse just year "2020"
        const yearMatch = dateStr.match(/(\d{4})/);
        if (yearMatch) {
          return { month: '', year: yearMatch[1] };
        }
        
        return { month: '', year: '' };
      };
      
      const startDate = parseDate(exp.start_date);
      const endDate = parseDate(exp.end_date);
      const isCurrent = exp.end_date === 'Present' || exp.end_date === 'Current' || !exp.end_date || exp.end_date.trim() === '';
      
      // Return in the format expected by the experience form
      return {
        title: exp.title || '',
        employment_type: exp.employment_type || '',
        company: exp.company || '',
        organization_logo: exp.organization_logo || '',
        is_current: isCurrent,
        start_month: startDate.month,
        start_year: startDate.year,
        end_month: isCurrent ? '' : endDate.month,
        end_year: isCurrent ? '' : endDate.year,
        location: exp.location || '',
        location_type: exp.location_type || '',
        description: exp.description || '',
        profile_headline: exp.profile_headline || '',
        job_source: 'LinkedIn Import',
        skills: exp.skills || [],
        media: exp.media || [],
        achievements: exp.achievements || []
      };
    });
    
    const currentExperiences = resume.experience || [];
    // Add transformed experiences to existing ones - each imported experience becomes a separate entry
    const updatedExperiences = [...currentExperiences, ...transformedExperiences];
    updateResume('experience', updatedExperiences);
  }, [resume, updateResume]);

  const handleProfileImport = useCallback((profileData) => {
    // Update basic profile fields from imported data
    if (profileData.title) updateResume('title', profileData.title);
    if (profileData.headline) updateResume('headline', profileData.headline);
    if (profileData.about) updateResume('summary', profileData.about); // 'summary' is the field name used in the form
    if (profileData.location) updateResume('location', profileData.location);
  }, [updateResume]);

  const handleEducationImport = useCallback((importedEducation) => {
    const currentEducation = resume.education || [];
    const updatedEducation = [...currentEducation, ...importedEducation];
    updateResume('education', updatedEducation);
  }, [resume, updateResume]);

  const handleCertificationsImport = useCallback((importedCerts) => {
    const currentCerts = resume.certifications || [];
    const currentDisplayCount = currentCerts.filter(c => c.display_on_profile).length;
    
    // Set display_on_profile for imported certs (only first ones up to limit of 10 total)
    const certsWithDisplay = importedCerts.map((cert, index) => ({
      ...cert,
      display_on_profile: (currentDisplayCount + index) < 10
    }));
    
    const updatedCerts = [...currentCerts, ...certsWithDisplay];
    updateResume('certifications', updatedCerts);
  }, [resume, updateResume]);

  const getDefaultItem = (field) => {
    switch (field) {
      case 'experience':
        return { 
          title: '', 
          employment_type: '', 
          company: '', 
          organization_logo: '',
          is_current: false,
          start_month: '', 
          start_year: '', 
          end_month: '', 
          end_year: '', 
          location: '', 
          location_type: '',
          description: '', 
          profile_headline: '',
          job_source: '',
          skills: [],
          media: [],
          achievements: [] 
        };
      case 'education':
        return { 
          degree: '', 
          field_of_study: '',
          institution: '', 
          institution_logo: '',
          location: '', 
          start_month: '', 
          start_year: '', 
          end_month: '', 
          end_year: '', 
          is_expected: false,
          start_date: '',
          end_date: '',
          gpa: '', 
          description: '' 
        };
      case 'training':
        return { name: '', provider: '', location: '', start_date: '', end_date: '', description: '', certificate_url: '' };
      case 'projects':
        return { name: '', url: '', description: '', technologies: [] };
      case 'certifications':
        return { 
          name: '', 
          issuer: '', 
          start_month: '',
          start_year: '',
          issue_month: '', 
          issue_year: '', 
          expiration_month: '', 
          expiration_year: '', 
          credential_id: '', 
          credential_url: '', 
          certification_logo: '',
          skills: [],
          display_on_profile: true, // Show on profile by default
          date: '', // Keep for backward compatibility
          url: '' // Keep for backward compatibility
        };
      case 'languages':
        return { name: '', proficiency: 'Native' };
      default:
        return {};
    }
  };

  return (
    <div className="space-y-4">
      {/* Basic Information */}
      <Section title="Basic Information" name="basic" expanded={expandedSections.basic} onToggle={toggleSection} hasData={sectionHasData.basic}>
        <div className="space-y-4">
          <ProfileImport 
            onImport={handleProfileImport}
            currentData={resume}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resume Title * (First, Last Name)
            </label>
            <input
              type="text"
              value={resume.title || ''}
              onChange={(e) => updateResume('title', e.target.value)}
              className="input"
              placeholder="e.g., My Professional Resume"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Professional Headline
            </label>
            <input
              type="text"
              value={resume.headline || ''}
              onChange={(e) => updateResume('headline', e.target.value)}
              className="input"
              placeholder="e.g., Retired U.S. Army Aviation Officer (Major) | Strategic Operations Leader"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Professional Summary (Profile Page)
            </label>
            <textarea
              value={resume.professional_summary || ''}
              onChange={(e) => updateResume('professional_summary', e.target.value)}
              className="textarea"
              rows="4"
              placeholder="Write a professional summary that will appear above your work experience on your profile page..."
            />
            <p className="text-xs text-gray-500 mt-1">
              This appears above the Work Experience section on your profile page.
            </p>
          </div>

          {/* Core Skills - two-column bullet list on resume, max 10 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Core Skills (max 10)
            </label>
            <div className="space-y-2">
              {(resume.core_skills || []).length > 0 && (
                <div className="grid grid-cols-2 gap-2">
                  {(resume.core_skills || []).map((skill, idx) => (
                    <div key={skill.id || idx} className="flex items-center gap-2 bg-gray-50 rounded px-3 py-1.5">
                      <span className="text-sm text-gray-700 flex-1">{skill.name || skill}</span>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = (resume.core_skills || []).filter((_, i) => i !== idx);
                          updateResume('core_skills', updated);
                        }}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {(resume.core_skills || []).length < 10 && (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    className="input flex-1"
                    placeholder="Add a core skill and press Enter..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const val = e.target.value.trim();
                        if (val) {
                          const current = resume.core_skills || [];
                          updateResume('core_skills', [...current, { id: Date.now(), name: val }]);
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-primary px-3 py-2"
                    onClick={(e) => {
                      const input = e.target.closest('.flex').querySelector('input');
                      const val = input.value.trim();
                      if (val && (resume.core_skills || []).length < 10) {
                        const current = resume.core_skills || [];
                        updateResume('core_skills', [...current, { id: Date.now(), name: val }]);
                        input.value = '';
                      }
                    }}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              )}
              <p className="text-xs text-gray-500">
                Displayed as a two-column bullet list on your resume. {(resume.core_skills || []).length}/10 added.
              </p>
            </div>
          </div>

          {!hideAbout && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              About
            </label>
            <textarea
              value={resume.summary || ''}
              onChange={(e) => updateResume('summary', e.target.value)}
              className="textarea"
              rows="4"
              placeholder="Write about yourself for your profile page..."
            />
            <p className="text-xs text-gray-500 mt-1">
              This appears in the "About" section on your profile page.
            </p>
          </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pronouns
            </label>
            <input
              type="text"
              value={resume.pronouns ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                console.log('Pronouns onChange:', value);
                updateResume('pronouns', value);
              }}
              onBlur={(e) => {
                console.log('Pronouns onBlur:', e.target.value, 'resume.pronouns:', resume.pronouns);
              }}
              className="input"
              placeholder="e.g., He/Him, She/Her, They/Them"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Credentials
            </label>
            <input
              type="text"
              value={resume.credentials ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                console.log('Credentials onChange:', value);
                updateResume('credentials', value);
              }}
              onBlur={(e) => {
                console.log('Credentials onBlur:', e.target.value, 'resume.credentials:', resume.credentials);
              }}
              className="input"
              placeholder="e.g., PMP, CSM®, MBA, PhD"
            />
            <p className="text-xs text-gray-500 mt-1">Separate multiple credentials with commas</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Highlight Color
            </label>
            <div className="flex items-center gap-3">
              <div className="flex-1 flex items-center gap-2">
                {HIGHLIGHT_COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => updateResume('highlight_color', color.value)}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      (resume.highlight_color || '#3b82f6') === color.value
                        ? 'border-gray-900 scale-110 ring-2 ring-gray-300'
                        : 'border-gray-300 hover:border-gray-500'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-gray-600" />
                <span className="text-sm text-gray-600">
                  {HIGHLIGHT_COLORS.find(c => c.value === (resume.highlight_color || '#3b82f6'))?.name || 'Blue'}
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Choose your accent color for highlights and skill badges</p>
          </div>
        </div>
      </Section>

      {/* Contact Information */}
      <Section title="Contact Information" name="contact" expanded={expandedSections.contact} onToggle={toggleSection} hasData={sectionHasData.contact}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={resume.contact_email || ''}
              onChange={(e) => updateResume('contact_email', e.target.value)}
              className="input"
              placeholder="your.email@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              value={resume.contact_phone || ''}
              onChange={(e) => updateResume('contact_phone', e.target.value)}
              className="input"
              placeholder="+1 (555) 123-4567"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <input
              type="text"
              value={resume.location || ''}
              onChange={(e) => updateResume('location', e.target.value)}
              className="input"
              placeholder="City, State, Country"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              LinkedIn URL
            </label>
            <input
              type="url"
              value={resume.linkedin_url || ''}
              onChange={(e) => updateResume('linkedin_url', e.target.value)}
              className="input"
              placeholder="https://linkedin.com/in/yourprofile"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Website URL
            </label>
            <input
              type="url"
              value={resume.website_url || ''}
              onChange={(e) => updateResume('website_url', e.target.value)}
              className="input"
              placeholder="https://yourwebsite.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Photo URL
            </label>
            <input
              type="url"
              value={resume.photo_url || ''}
              onChange={(e) => updateResume('photo_url', e.target.value)}
              className="input"
              placeholder="https://example.com/photo.jpg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Banner URL
            </label>
            <input
              type="url"
              value={resume.banner_url || ''}
              onChange={(e) => updateResume('banner_url', e.target.value)}
              className="input"
              placeholder="https://example.com/banner.jpg"
            />
          </div>
        </div>
      </Section>

      {/* Professional Details */}
      <Section title="Professional Details" name="professional" expanded={expandedSections.professional} onToggle={toggleSection} hasData={sectionHasData.professional}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Role
            </label>
            <input
              type="text"
              value={resume.target_role || ''}
              onChange={(e) => updateResume('target_role', e.target.value)}
              className="input"
              placeholder="e.g., Retired U.S. Army Aviation Officer (Major)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Industry
            </label>
            <select
              value={resume.target_industry || ''}
              onChange={(e) => updateResume('target_industry', e.target.value)}
              className="select"
            >
              <option value="">Select an industry</option>
              {INDUSTRIES.map((industry) => (
                <option key={industry} value={industry}>
                  {industry}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Seniority Level
            </label>
            <select
              value={resume.seniority_level || ''}
              onChange={(e) => updateResume('seniority_level', e.target.value)}
              className="select"
            >
              <option value="">Select a level</option>
              {SENIORITY_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={resume.is_primary || false}
              onChange={(e) => updateResume('is_primary', e.target.checked)}
              className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
            />
            <label className="ml-2 text-sm font-medium text-gray-700">
              Set as primary resume
            </label>
          </div>
        </div>
      </Section>

      {/* Experience */}
      <Section title="Work Experience" name="experience" expanded={expandedSections.experience} onToggle={toggleSection} hasData={sectionHasData.experience}>
        <div className="space-y-4">
          <LinkedInImport 
            onImport={handleLinkedInImport}
            existingCount={(resume.experience || []).length}
          />
          {(resume.experience || []).map((exp, index) => {
            const descriptionLength = (exp.description || '').length;
            const maxDescriptionLength = 2500;
            const hasData = hasExperienceData(exp);
            // Default to collapsed (See more) when hasData to reduce scrolling; empty entries stay expanded.
            // Stored true = explicitly expanded, false/undefined = collapsed.
            const isCollapsed = hasData ? (collapsedExperiences[index] !== true) : false;
            const formatDate = (month, year) => {
              if (!month || !year) return '';
              return `${month} ${year}`;
            };
            const startDate = exp.start_month && exp.start_year ? formatDate(exp.start_month, exp.start_year) : '';
            const endDate = exp.is_current ? 'Present' : (exp.end_month && exp.end_year ? formatDate(exp.end_month, exp.end_year) : '');
            const dateRange = startDate ? `${startDate}${endDate ? ` - ${endDate}` : ''}` : '';
            
            return (
              <div key={index} className="border border-gray-200 rounded-lg p-6 space-y-4">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1 flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-gray-900">Work Experience {index + 1}</h4>
                    {hasData && (
                      <button
                        onClick={() => toggleExperienceCollapse(index)}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                        type="button"
                      >
                        {isCollapsed ? 'See more' : 'See less'}
                      </button>
                    )}
                    {isCollapsed && hasData && (
                      <div className="w-full mt-2 text-sm text-gray-600 space-y-1">
                        {exp.title && <div className="font-medium">{exp.title}</div>}
                        {exp.company && <div>{exp.company}</div>}
                        {dateRange && <div className="text-xs">{dateRange}</div>}
                      </div>
                    )}
                  </div>
                <button
                  onClick={() => removeItem('experience', index)}
                    className="text-red-600 hover:text-red-800 flex-shrink-0"
                    type="button"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
                
                {!isCollapsed && (
                <div className="space-y-4">
                  {/* Auto-Fill Feature */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-5 h-5 text-blue-600" />
                      <label className="block text-sm font-medium text-blue-900">
                        Auto-Fill from Text
                      </label>
                    </div>
                    <p className="text-xs text-blue-700 mb-3">
                      Paste your job information here and we'll automatically extract and fill in all fields including dates, location, and skills.
                    </p>
                    <textarea
                      value={autoFillText[index] || ''}
                      onChange={(e) => setAutoFillText(prev => ({ ...prev, [index]: e.target.value }))}
                      className="w-full p-3 border border-blue-300 rounded-md text-sm mb-2"
                      rows="4"
                      placeholder="Paste job information here, e.g.:&#10;Software Engineer&#10;Microsoft&#10;Jan 2020 - Present&#10;Seattle, WA&#10;Led development of new features..."
                    />
                    <button
                      type="button"
                      onClick={() => parseAndAutoFill(autoFillText[index] || '', index)}
                      disabled={!autoFillText[index] || !autoFillText[index].trim()}
                      className="btn btn-primary text-sm inline-flex items-center gap-2"
                    >
                      <Sparkles className="w-4 h-4" />
                      Auto-Fill All Fields
                    </button>
                  </div>
                  
                  {/* Title - Required */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                <input
                  type="text"
                  value={exp.title || ''}
                  onChange={(e) => updateItem('experience', index, 'title', e.target.value)}
                  className="input"
                      placeholder="Ex: Retail Sales Manager"
                      required
                    />
                  </div>

                  {/* Employment Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Employment type
                    </label>
                    <select
                      value={exp.employment_type || ''}
                      onChange={(e) => updateItem('experience', index, 'employment_type', e.target.value)}
                      className="select"
                    >
                      <option value="">Please select</option>
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                      <option value="Temporary">Temporary</option>
                      <option value="Internship">Internship</option>
                      <option value="Volunteer">Volunteer</option>
                      <option value="Freelance">Freelance</option>
                    </select>
                  </div>

                  {/* Company - Required */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company or organization <span className="text-red-500">*</span>
                    </label>
                <input
                  type="text"
                  value={exp.company || ''}
                  onChange={(e) => updateItem('experience', index, 'company', e.target.value)}
                  className="input"
                      placeholder="Ex: Microsoft"
                      required
                    />
                  </div>

                  {/* Organization logo - optional */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Organization logo
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Shown next to title and company on your profile. Use a square image (e.g. company logo).
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                        {exp.organization_logo ? (
                          <img src={exp.organization_logo} alt="Logo" className="w-full h-full object-contain" />
                        ) : (
                          <ImageIcon className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 space-y-2">
                <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file && file.type.startsWith('image/')) {
                              try {
                                const cloudFrontUrl = await resumeAPI.uploadImageToS3(file, 'avatar');
                                updateItem('experience', index, 'organization_logo', cloudFrontUrl);
                              } catch (error) {
                                console.error('Failed to upload organization logo:', error);
                                alert('Failed to upload logo. Please try again.');
                              }
                            }
                            e.target.value = '';
                          }}
                          className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                        />
                        <div className="flex gap-2 items-center flex-wrap">
                  <input
                            type="url"
                            value={exp.organization_logo && exp.organization_logo.startsWith('http') ? exp.organization_logo : ''}
                            onChange={(e) => updateItem('experience', index, 'organization_logo', e.target.value || '')}
                            className="input flex-1 min-w-[200px]"
                            placeholder="Or paste image URL"
                          />
                          {exp.organization_logo && (
                            <button
                              type="button"
                              onClick={() => updateItem('experience', index, 'organization_logo', '')}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Remove logo
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Currently Working Checkbox */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exp.is_current || false}
                      onChange={(e) => {
                        updateItem('experience', index, 'is_current', e.target.checked);
                        if (e.target.checked) {
                          updateItem('experience', index, 'end_month', '');
                          updateItem('experience', index, 'end_year', '');
                        }
                      }}
                      className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                      id={`current-${index}`}
                    />
                    <label htmlFor={`current-${index}`} className="ml-2 text-sm font-medium text-gray-700">
                      I am currently working in this role <span className="text-red-500">*</span>
                    </label>
                  </div>

                  {/* Start Date - Required */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start date <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Month of Start date</label>
                        <select
                          value={exp.start_month || ''}
                          onChange={(e) => updateItem('experience', index, 'start_month', e.target.value)}
                          className="select"
                          required
                        >
                          <option value="">Month</option>
                          <option value="Jan">January</option>
                          <option value="Feb">February</option>
                          <option value="Mar">March</option>
                          <option value="Apr">April</option>
                          <option value="May">May</option>
                          <option value="Jun">June</option>
                          <option value="Jul">July</option>
                          <option value="Aug">August</option>
                          <option value="Sep">September</option>
                          <option value="Oct">October</option>
                          <option value="Nov">November</option>
                          <option value="Dec">December</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Year of Start date</label>
                        <select
                          value={exp.start_year || ''}
                          onChange={(e) => updateItem('experience', index, 'start_year', e.target.value)}
                          className="select"
                          required
                        >
                          <option value="">Year</option>
                          {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i).map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* End Date - Required (unless currently working) */}
                  {!exp.is_current && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End date <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Month of End date</label>
                          <select
                            value={exp.end_month || ''}
                            onChange={(e) => updateItem('experience', index, 'end_month', e.target.value)}
                            className="select"
                            required={!exp.is_current}
                          >
                            <option value="">Month</option>
                            <option value="Jan">January</option>
                            <option value="Feb">February</option>
                            <option value="Mar">March</option>
                            <option value="Apr">April</option>
                            <option value="May">May</option>
                            <option value="Jun">June</option>
                            <option value="Jul">July</option>
                            <option value="Aug">August</option>
                            <option value="Sep">September</option>
                            <option value="Oct">October</option>
                            <option value="Nov">November</option>
                            <option value="Dec">December</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Year of End date</label>
                          <select
                            value={exp.end_year || ''}
                            onChange={(e) => updateItem('experience', index, 'end_year', e.target.value)}
                            className="select"
                            required={!exp.is_current}
                          >
                            <option value="">Year</option>
                            {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i).map(year => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Location */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location
                    </label>
                  <input
                    type="text"
                  value={exp.location || ''}
                  onChange={(e) => updateItem('experience', index, 'location', e.target.value)}
                    className="input"
                      placeholder="Ex: London, United Kingdom"
                  />
                </div>

                  {/* Location Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location type
                    </label>
                    <select
                      value={exp.location_type || ''}
                      onChange={(e) => updateItem('experience', index, 'location_type', e.target.value)}
                      className="select"
                    >
                      <option value="">Please select</option>
                      <option value="On-site">On-site</option>
                      <option value="Remote">Remote</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Pick a location type (ex: remote)</p>
                  </div>

                  {/* Description */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      {exp.description && (
                        <button
                          type="button"
                          onClick={() => handleAiRewrite(index)}
                          disabled={aiRewritingIndex === index}
                          className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 hover:text-purple-800 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="AI: Rewrite description to be more professional and impactful"
                        >
                          {aiRewritingIndex === index ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Wand2 className="w-3.5 h-3.5" />
                          )}
                          {aiRewritingIndex === index ? 'Rewriting...' : 'AI Rewrite'}
                        </button>
                      )}
                    </div>
                <textarea
                  value={exp.description || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= maxDescriptionLength) {
                          updateItem('experience', index, 'description', value);
                        }
                      }}
                  className="textarea"
                      rows="6"
                      placeholder="List your major duties and successes, highlighting specific projects"
                      maxLength={maxDescriptionLength}
                />
                    <div className="flex justify-between items-center mt-1">
                      <p className="text-xs text-gray-500">
                        {descriptionLength}/{maxDescriptionLength} maximum characters allowed.
                      </p>
              </div>
                    {/* AI Rewrite Suggestion */}
                    {aiRewriteResult[index] && (
                      <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Wand2 className="w-4 h-4 text-purple-600" />
                          <span className="text-sm font-medium text-purple-900">AI Suggestion</span>
                        </div>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap mb-3">{aiRewriteResult[index]}</p>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleAcceptRewrite(index)}
                            className="px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700"
                          >
                            Accept
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDismissRewrite(index)}
                            className="px-3 py-1.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    )}
            </div>

                  {/* Job Source */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Where did you find this job?
                    </label>
                    <select
                      value={exp.job_source || ''}
                      onChange={(e) => updateItem('experience', index, 'job_source', e.target.value)}
                      className="select"
                    >
                      <option value="">Please select</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Company Website">Company Website</option>
                      <option value="Job Board">Job Board</option>
                      <option value="Recruiter">Recruiter</option>
                      <option value="Referral">Referral</option>
                      <option value="Networking Event">Networking Event</option>
                      <option value="Other">Other</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      This information will be used to improve job search experience.
                    </p>
                  </div>

                  {/* Skills */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Skills
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      We recommend adding your top 5 used in this role. They'll also appear in your Skills section.
                    </p>
                    <div className="space-y-2">
                      {(exp.skills || []).map((skill, skillIndex) => {
                        const inputKey = `${index}-${skillIndex}`;
                        // Show the input state value if it exists, otherwise show the skill (even if empty)
                        const inputValue = skillsInputState[inputKey] !== undefined 
                          ? skillsInputState[inputKey] 
                          : (skill || '');
                        const matchingSkills = inputValue && inputValue.trim() 
                          ? getMatchingSkills(inputValue, userSkills)
                          : [];
                        const showDropdown = matchingSkills.length > 0 && inputValue.trim() !== skill;
                        
                        return (
                          <div key={skillIndex} className="relative flex items-center gap-2">
                            <div className="flex-1 relative">
                  <input
                    type="text"
                                value={inputValue}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setSkillsInputState(prev => ({ ...prev, [inputKey]: value }));
                                }}
                                onBlur={() => {
                                  // Update the skill when input loses focus
                                  const finalValue = (skillsInputState[inputKey] !== undefined 
                                    ? skillsInputState[inputKey] 
                                    : skill || '').trim();
                                  const newSkills = [...(exp.skills || [])];
                                  if (finalValue) {
                                    // Update with the new value
                                    newSkills[skillIndex] = finalValue;
                                    updateItem('experience', index, 'skills', newSkills);
                                  } else {
                                    // Remove empty skills only if they were already empty (not just typed)
                                    if (!skill || !skill.trim()) {
                                      newSkills.splice(skillIndex, 1);
                                      updateItem('experience', index, 'skills', newSkills);
                                    }
                                  }
                                  // Clear input state after a delay to allow dropdown click
                                  setTimeout(() => {
                                    setSkillsInputState(prev => {
                                      const updated = { ...prev };
                                      delete updated[inputKey];
                                      return updated;
                                    });
                                  }, 200);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && matchingSkills.length > 0) {
                                    e.preventDefault();
                                    const selectedSkill = matchingSkills[0];
                                    const newSkills = [...(exp.skills || [])];
                                    newSkills[skillIndex] = selectedSkill;
                                    updateItem('experience', index, 'skills', newSkills);
                                    setSkillsInputState(prev => {
                                      const updated = { ...prev };
                                      delete updated[inputKey];
                                      return updated;
                                    });
                                  }
                                }}
                                className="input w-full"
                                placeholder="Add skill"
                              />
                              {showDropdown && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                  {userSkills.length > 0 && (
                                    <div className="p-2 border-b border-gray-200">
                                      <p className="text-xs font-semibold text-gray-500 mb-1">Your Skills</p>
                                      {userSkills.filter(s => s.toLowerCase().includes(inputValue.toLowerCase())).map((userSkill, idx) => (
                                        <button
                                          key={idx}
                                          type="button"
                                          onClick={() => {
                                            const newSkills = [...(exp.skills || [])];
                                            newSkills[skillIndex] = userSkill;
                                            updateItem('experience', index, 'skills', newSkills);
                                            setSkillsInputState(prev => {
                                              const updated = { ...prev };
                                              delete updated[inputKey];
                                              return updated;
                                            });
                                          }}
                                          className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm"
                                        >
                                          {userSkill}
                                        </button>
                                      ))}
                </div>
                                  )}
                                  <div className="p-2">
                                    {matchingSkills.slice(0, 10).map((matchSkill, idx) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        onClick={() => {
                                          const newSkills = [...(exp.skills || [])];
                                          newSkills[skillIndex] = matchSkill;
                                          updateItem('experience', index, 'skills', newSkills);
                                          setSkillsInputState(prev => {
                                            const updated = { ...prev };
                                            delete updated[inputKey];
                                            return updated;
                                          });
                                        }}
                                        className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm"
                                      >
                                        {matchSkill}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const currentSkills = exp.skills || [];
                                const newSkills = currentSkills.filter((_, i) => i !== skillIndex);
                                updateItem('experience', index, 'skills', newSkills);
                                setSkillsInputState(prev => {
                                  const updated = { ...prev };
                                  delete updated[inputKey];
                                  return updated;
                                });
                              }}
                              className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                              title="Remove this skill"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => {
                          const newSkills = [...(exp.skills || []), ''];
                          updateItem('experience', index, 'skills', newSkills);
                        }}
                        className="btn btn-outline text-sm"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add skill
                      </button>
                    </div>
                  </div>

                  {/* Media */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Media
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Add media like images, documents, sites or presentations.
                    </p>
                    <div className="space-y-2">
                      {(exp.media || []).map((mediaItem, mediaIndex) => {
                        const uploadInputId = `media-upload-${index}-${mediaIndex}`;
                        const isUploadType = mediaItem.type === 'upload';
                        const hasFile = mediaItem.url && mediaItem.url.startsWith('blob:');
                        const fileName = mediaItem.fileName || (hasFile ? 'File uploaded' : '');
                        
                        return (
                          <div key={mediaIndex} className="flex items-center gap-2">
                            {isUploadType ? (
                              <>
                                <input
                                  type="file"
                                  accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      // Validate file size (max 10MB)
                                      const maxSize = 10 * 1024 * 1024; // 10MB
                                      if (file.size > maxSize) {
                                        alert(`File size exceeds 10MB limit. Please choose a smaller file.`);
                                        e.target.value = ''; // Reset input
                                        return;
                                      }
                                      
                                      // Create object URL for preview/display
                                      const fileUrl = URL.createObjectURL(file);
                                      const newMedia = [...(exp.media || [])];
                                      newMedia[mediaIndex] = { 
                                        ...newMedia[mediaIndex], 
                                        url: fileUrl,
                                        fileName: file.name,
                                        fileSize: file.size,
                                        fileType: file.type,
                                        title: mediaItem.title || file.name
                                      };
                                      updateItem('experience', index, 'media', newMedia);
                                    }
                                  }}
                                  className="hidden"
                                  id={uploadInputId}
                                />
                                <div className="flex-1 flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={fileName || 'No file selected'}
                                    readOnly
                                    className="input flex-1 bg-gray-50"
                                    placeholder="No file selected"
                                  />
                                  <label
                                    htmlFor={uploadInputId}
                                    className="btn btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
                                  >
                                    {hasFile ? 'Change File' : 'Choose File'}
                                  </label>
              </div>
                              </>
                            ) : (
                              <input
                                type="url"
                                value={mediaItem.url || ''}
                                onChange={(e) => {
                                  const newMedia = [...(exp.media || [])];
                                  newMedia[mediaIndex] = { ...newMedia[mediaIndex], url: e.target.value };
                                  updateItem('experience', index, 'media', newMedia);
                                }}
                                className="input flex-1"
                                placeholder="Enter link URL (video, article, website)"
                              />
                            )}
                            <input
                              type="text"
                              value={mediaItem.title || ''}
                              onChange={(e) => {
                                const newMedia = [...(exp.media || [])];
                                newMedia[mediaIndex] = { ...newMedia[mediaIndex], title: e.target.value };
                                updateItem('experience', index, 'media', newMedia);
                              }}
                              className="input w-32"
                              placeholder="Title"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                // Clean up object URL if it exists
                                if (mediaItem.url && mediaItem.url.startsWith('blob:')) {
                                  URL.revokeObjectURL(mediaItem.url);
                                }
                                const newMedia = (exp.media || []).filter((_, i) => i !== mediaIndex);
                                updateItem('experience', index, 'media', newMedia);
                              }}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
            </div>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => setMediaModalOpen({ expIndex: index, mediaIndex: null })}
                        className="btn btn-outline text-sm"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add media
                      </button>
                    </div>
                  </div>
                  
                  {/* Media Modal */}
                  {mediaModalOpen.expIndex === index && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setMediaModalOpen({ expIndex: null, mediaIndex: null })}>
                      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-semibold text-gray-900">Add Media</h3>
                          <button
                            type="button"
                            onClick={() => setMediaModalOpen({ expIndex: null, mediaIndex: null })}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="space-y-3">
                          <button
                            type="button"
                            onClick={() => {
                              const newMedia = [...(exp.media || []), { url: '', title: '', type: 'link' }];
                              updateItem('experience', index, 'media', newMedia);
                              setMediaModalOpen({ expIndex: null, mediaIndex: null });
                            }}
                            className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
                          >
                            <LinkIcon className="w-6 h-6 text-gray-600 flex-shrink-0" />
                            <div>
                              <div className="font-medium text-gray-900">Add a link</div>
                              <div className="text-sm text-gray-500">Use for videos, articles, and websites</div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const newMedia = [...(exp.media || []), { url: '', title: '', type: 'upload' }];
                              updateItem('experience', index, 'media', newMedia);
                              setMediaModalOpen({ expIndex: null, mediaIndex: null });
                            }}
                            className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
                          >
                            <ImageIcon className="w-6 h-6 text-gray-600 flex-shrink-0" />
                            <div>
                              <div className="font-medium text-gray-900">Add media</div>
                              <div className="text-sm text-gray-500">Upload pictures, presentations, or documents</div>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>
            );
          })}
          <button
            onClick={() => addItem('experience')}
            className="btn btn-outline w-full inline-flex items-center justify-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Add Experience</span>
          </button>
        </div>
      </Section>

      {/* Education */}
      <Section title="Education" name="education" expanded={expandedSections.education} onToggle={toggleSection} hasData={sectionHasData.education}>
        <div className="space-y-4">
          <EducationImport 
            onImport={handleEducationImport}
            existingCount={(resume.education || []).length}
          />
          {(resume.education || []).map((edu, index) => {
            const isCollapsed = collapsedEducation[index] === true;
            const hasData = hasEducationData(edu);
            const formatDate = (month, year) => {
              if (!month || !year) return '';
              return `${month} ${year}`;
            };
            const startDate = edu.start_month && edu.start_year ? formatDate(edu.start_month, edu.start_year) : '';
            const endDate = edu.is_expected ? 'Expected' : (edu.end_month && edu.end_year ? formatDate(edu.end_month, edu.end_year) : '');
            const dateRange = startDate ? `${startDate}${endDate ? ` - ${endDate}` : ''}` : '';
            
            return (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                  <div className="flex-1 flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-gray-900">Education {index + 1}</h4>
                    {hasData && (
                      <button
                        onClick={() => toggleEducationCollapse(index)}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                        type="button"
                      >
                        {isCollapsed ? 'See more' : 'See less'}
                      </button>
                    )}
                    {isCollapsed && hasData && (
                      <div className="w-full mt-2 text-sm text-gray-600 space-y-1">
                        {edu.degree && <div className="font-medium">{edu.degree}</div>}
                        {edu.field_of_study && <div>{edu.field_of_study}</div>}
                        {edu.institution && <div>{edu.institution}</div>}
                        {dateRange && <div className="text-xs">{dateRange}</div>}
                      </div>
                    )}
                  </div>
                <button
                  onClick={() => removeItem('education', index)}
                    className="text-red-600 hover:text-red-800 flex-shrink-0"
                    type="button"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
                {!isCollapsed && (
                <div className="space-y-4">
                <input
                  type="text"
                  value={edu.institution || ''}
                  onChange={(e) => updateItem('education', index, 'institution', e.target.value)}
                  className="input"
                  placeholder="Institution Name"
                />

                {/* Institution Logo Upload */}
                <div className="border border-gray-200 rounded-lg p-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Institution Logo</label>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                      {edu.institution_logo ? (
                        <img src={edu.institution_logo} alt="Logo" className="w-full h-full object-contain" />
                      ) : (
                        <ImageIcon className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file && file.type.startsWith('image/')) {
                            try {
                              const cloudFrontUrl = await resumeAPI.uploadImageToS3(file, 'avatar');
                              updateItem('education', index, 'institution_logo', cloudFrontUrl);
                            } catch (error) {
                              console.error('Failed to upload institution logo:', error);
                              alert('Failed to upload logo. Please try again.');
                            }
                          }
                          e.target.value = '';
                        }}
                        className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                      />
                      <div className="flex gap-2 items-center flex-wrap">
                        <input
                          type="url"
                          value={edu.institution_logo && edu.institution_logo.startsWith('http') ? edu.institution_logo : ''}
                          onChange={(e) => updateItem('education', index, 'institution_logo', e.target.value || '')}
                          className="input flex-1 min-w-[200px]"
                          placeholder="Or paste image URL"
                        />
                        {edu.institution_logo && (
                          <button
                            type="button"
                            onClick={() => updateItem('education', index, 'institution_logo', '')}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Remove logo
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <input
                  type="text"
                  value={edu.degree || ''}
                  onChange={(e) => updateItem('education', index, 'degree', e.target.value)}
                  className="input"
                  placeholder="Degree (e.g., Bachelor of Science in Computer Science)"
                />
                <input
                  type="text"
                  value={edu.field_of_study || ''}
                  onChange={(e) => updateItem('education', index, 'field_of_study', e.target.value)}
                  className="input"
                  placeholder="Field of study (e.g., Computer Science)"
                />
                
                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start date
                  </label>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Month of Start date</label>
                      <select
                        value={edu.start_month || ''}
                        onChange={(e) => updateItem('education', index, 'start_month', e.target.value)}
                        className="select"
                      >
                        <option value="">Month</option>
                        <option value="Jan">January</option>
                        <option value="Feb">February</option>
                        <option value="Mar">March</option>
                        <option value="Apr">April</option>
                        <option value="May">May</option>
                        <option value="Jun">June</option>
                        <option value="Jul">July</option>
                        <option value="Aug">August</option>
                        <option value="Sep">September</option>
                        <option value="Oct">October</option>
                        <option value="Nov">November</option>
                        <option value="Dec">December</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Year of Start date</label>
                      <select
                        value={edu.start_year || ''}
                        onChange={(e) => updateItem('education', index, 'start_year', e.target.value)}
                        className="select"
                      >
                        <option value="">Year</option>
                        {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i).map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* End Date (or Expected) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End date {edu.is_expected ? '(or expected)' : ''}
                  </label>
                  <div className="mb-2">
                    <label className="flex items-center cursor-pointer">
                  <input
                        type="checkbox"
                        checked={edu.is_expected || false}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateItem('education', index, 'is_expected', e.target.checked);
                          if (e.target.checked) {
                            updateItem('education', index, 'end_month', '');
                            updateItem('education', index, 'end_year', '');
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 mr-2 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700 cursor-pointer" onClick={(e) => {
                        e.stopPropagation();
                        updateItem('education', index, 'is_expected', !edu.is_expected);
                        if (!edu.is_expected) {
                          updateItem('education', index, 'end_month', '');
                          updateItem('education', index, 'end_year', '');
                        }
                      }}>This is an expected end date</span>
                    </label>
                  </div>
                  {!edu.is_expected && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Month of End date</label>
                        <select
                          value={edu.end_month || ''}
                          onChange={(e) => updateItem('education', index, 'end_month', e.target.value)}
                          className="select"
                        >
                          <option value="">Month</option>
                          <option value="Jan">January</option>
                          <option value="Feb">February</option>
                          <option value="Mar">March</option>
                          <option value="Apr">April</option>
                          <option value="May">May</option>
                          <option value="Jun">June</option>
                          <option value="Jul">July</option>
                          <option value="Aug">August</option>
                          <option value="Sep">September</option>
                          <option value="Oct">October</option>
                          <option value="Nov">November</option>
                          <option value="Dec">December</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Year of End date</label>
                        <select
                          value={edu.end_year || ''}
                          onChange={(e) => updateItem('education', index, 'end_year', e.target.value)}
                          className="select"
                        >
                          <option value="">Year</option>
                          {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i).map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                  {edu.is_expected && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Month of End date (or expected)</label>
                        <select
                          value={edu.end_month || ''}
                          onChange={(e) => updateItem('education', index, 'end_month', e.target.value)}
                          className="select"
                        >
                          <option value="">Month</option>
                          <option value="Jan">January</option>
                          <option value="Feb">February</option>
                          <option value="Mar">March</option>
                          <option value="Apr">April</option>
                          <option value="May">May</option>
                          <option value="Jun">June</option>
                          <option value="Jul">July</option>
                          <option value="Aug">August</option>
                          <option value="Sep">September</option>
                          <option value="Oct">October</option>
                          <option value="Nov">November</option>
                          <option value="Dec">December</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Year of End date (or expected)</label>
                        <select
                          value={edu.end_year || ''}
                          onChange={(e) => updateItem('education', index, 'end_year', e.target.value)}
                          className="select"
                        >
                          <option value="">Year</option>
                          {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() + i).map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                  <input
                    type="text"
                  value={edu.location || ''}
                  onChange={(e) => updateItem('education', index, 'location', e.target.value)}
                    className="input"
                  placeholder="Location (optional)"
                  />
                <input
                  type="text"
                  value={edu.gpa || ''}
                  onChange={(e) => updateItem('education', index, 'gpa', e.target.value)}
                  className="input"
                  placeholder="GPA (optional)"
                />
                {edu.description && (
                  <textarea
                    value={edu.description || ''}
                    onChange={(e) => updateItem('education', index, 'description', e.target.value)}
                    className="textarea"
                    rows="3"
                    placeholder="Description (optional)"
                  />
                )}
                <input
                  type="text"
                  value={edu.activities_and_societies || ''}
                  onChange={(e) => updateItem('education', index, 'activities_and_societies', e.target.value)}
                  className="input"
                  placeholder="Activities and societies (optional)"
                />
              </div>
                )}
            </div>
            );
          })}
          <button
            onClick={() => addItem('education')}
            className="btn btn-outline w-full inline-flex items-center justify-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Add Education</span>
          </button>
        </div>
      </Section>

      {/* Training */}
      <Section title="Training & Professional Development" name="training" expanded={expandedSections.training} onToggle={toggleSection} hasData={sectionHasData.training}>
        <div className="space-y-4">
          {(resume.training || []).map((training, index) => {
            const isCollapsed = collapsedTraining[index] === true;
            const hasData = hasTrainingData(training);
            
            return (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                  <div className="flex-1 flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-gray-900">Training {index + 1}</h4>
                    {hasData && (
                      <button
                        onClick={() => toggleTrainingCollapse(index)}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                        type="button"
                      >
                        {isCollapsed ? 'See more' : 'See less'}
                      </button>
                    )}
                    {isCollapsed && hasData && (
                      <div className="w-full mt-2 text-sm text-gray-600 space-y-1">
                        {training.name && <div className="font-medium">{training.name}</div>}
                        {training.provider && <div>{training.provider}</div>}
                        {(training.start_date || training.end_date) && (
                          <div className="text-xs">
                            {training.start_date}{training.end_date ? ` - ${training.end_date}` : ''}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                <button
                  onClick={() => removeItem('training', index)}
                    className="text-red-600 hover:text-red-800 flex-shrink-0"
                    type="button"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
                {!isCollapsed && (
              <div className="space-y-3">
                <input
                  type="text"
                  value={training.name || ''}
                  onChange={(e) => updateItem('training', index, 'name', e.target.value)}
                  className="input"
                  placeholder="Training/Course Name"
                />
                <input
                  type="text"
                  value={training.provider || ''}
                  onChange={(e) => updateItem('training', index, 'provider', e.target.value)}
                  className="input"
                  placeholder="Provider/Organization"
                />
                <input
                  type="text"
                  value={training.location || ''}
                  onChange={(e) => updateItem('training', index, 'location', e.target.value)}
                  className="input"
                  placeholder="Location (optional)"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={training.start_date || ''}
                    onChange={(e) => updateItem('training', index, 'start_date', e.target.value)}
                    className="input"
                    placeholder="Start Date"
                  />
                  <input
                    type="text"
                    value={training.end_date || ''}
                    onChange={(e) => updateItem('training', index, 'end_date', e.target.value)}
                    className="input"
                    placeholder="End Date"
                  />
                </div>
                <textarea
                  value={training.description || ''}
                  onChange={(e) => updateItem('training', index, 'description', e.target.value)}
                  className="textarea"
                  rows="2"
                  placeholder="Description (optional)"
                />
                <input
                  type="url"
                  value={training.certificate_url || ''}
                  onChange={(e) => updateItem('training', index, 'certificate_url', e.target.value)}
                  className="input"
                  placeholder="Certificate URL (optional)"
                />
              </div>
                )}
            </div>
            );
          })}
          <button
            onClick={() => addItem('training')}
            className="btn btn-outline w-full inline-flex items-center justify-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Add Training</span>
          </button>
        </div>
      </Section>

      {/* Skills */}
      <Section title="Skills" name="skills" expanded={expandedSections.skills} onToggle={toggleSection} hasData={sectionHasData.skills}>
        <div className="space-y-3">
          <textarea
            value={(resume.skills || []).join(', ')}
            onChange={(e) => updateResume('skills', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
            className="textarea"
            rows="4"
            placeholder="Enter skills separated by commas (e.g., JavaScript, React, Node.js, Python)"
          />
          <p className="text-sm text-gray-600">Separate skills with commas</p>

          {/* AI Skill Suggestions */}
          <div className="pt-2">
              <button
                type="button"
                onClick={handleAiSuggestSkills}
                disabled={aiSuggestingSkills}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {aiSuggestingSkills ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Lightbulb className="w-4 h-4" />
                )}
                {aiSuggestingSkills ? 'Suggesting...' : 'AI: Suggest Skills'}
              </button>

              {aiSuggestedSkills.length > 0 && (
                <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-900">Suggested Skills</span>
                    <span className="text-xs text-purple-600">(click to add)</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {aiSuggestedSkills.map((skill, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleAddSuggestedSkill(skill)}
                        className="px-3 py-1.5 text-xs font-medium bg-white border border-purple-300 rounded-full text-purple-700 hover:bg-purple-600 hover:text-white transition-colors"
                      >
                        + {skill}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

          {/* AI Error message */}
          {aiError && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {aiError}
              <button
                type="button"
                onClick={() => setAiError('')}
                className="ml-2 text-red-500 hover:text-red-700"
              >
                <X className="w-4 h-4 inline" />
              </button>
            </div>
          )}
        </div>
      </Section>

      {/* Projects section hidden per user request - removed for all users */}

      {/* License or Certification */}
      <Section title="License or Certification" name="certifications" expanded={expandedSections.certifications} onToggle={toggleSection} hasData={sectionHasData.certifications}>
        <div className="space-y-4">
          <CertificationsImport 
            onImport={handleCertificationsImport}
            existingCount={(resume.certifications || []).length}
          />
          
          {/* Display counter info */}
          {(resume.certifications || []).length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-900">
                <span className="font-semibold">
                  {(resume.certifications || []).filter(c => c.display_on_profile).length} of {(resume.certifications || []).length}
                </span> certification{(resume.certifications || []).length !== 1 ? 's' : ''} will display on your public profile.
                {(resume.certifications || []).filter(c => c.display_on_profile).length >= 10 && (
                  <span className="block mt-1 text-xs">Maximum of 10 certifications can be displayed.</span>
                )}
              </p>
            </div>
          )}
          
          {(resume.certifications || []).map((cert, index) => {
            const isCollapsed = collapsedCertifications[index] === true;
            const hasData = hasCertificationData(cert);
            
            return (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                  <div className="flex-1 flex items-center gap-3 flex-wrap">
                    <h4 className="font-semibold text-gray-900">License or Certification {index + 1}</h4>
                    
                    {/* Display on profile checkbox */}
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={cert.display_on_profile !== false}
                        onChange={(e) => {
                          const currentDisplayCount = (resume.certifications || []).filter(c => c.display_on_profile).length;
                          if (e.target.checked && currentDisplayCount >= 10) {
                            alert('Maximum of 10 certifications can be displayed on your profile. Please uncheck another certification first.');
                            e.target.checked = false;
                            return;
                          }
                          updateItem('certifications', index, 'display_on_profile', e.target.checked);
                        }}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-gray-700">Display on profile</span>
                    </label>
                    
                    {hasData && (
                      <button
                        onClick={() => toggleCertificationCollapse(index)}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                        type="button"
                      >
                        {isCollapsed ? 'See more' : 'See less'}
                      </button>
                    )}
                    {isCollapsed && hasData && (
                      <div className="w-full mt-2 text-sm text-gray-600 space-y-1">
                        {cert.name && <div className="font-medium">{cert.name}</div>}
                        {cert.issuer && <div>{cert.issuer}</div>}
                        {(cert.issue_month && cert.issue_year) && <div className="text-xs">{cert.issue_month} {cert.issue_year}</div>}
                        {cert.date && <div className="text-xs">{cert.date}</div>}
                      </div>
                    )}
                  </div>
                <button
                  onClick={() => removeItem('certifications', index)}
                    className="text-red-600 hover:text-red-800 flex-shrink-0"
                    type="button"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
                {!isCollapsed && (
                <div className="space-y-4">
                  {/* Certification Logo/Icon Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Certification Logo/Icon
                    </label>
                    <div className="flex items-center gap-4">
                      <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                        {cert.certification_logo ? (
                          <>
                            <img src={cert.certification_logo} alt={cert.name ? `${cert.name} logo` : 'Certification'} className="w-full h-full object-contain" />
                            <input type="file" accept="image/*" className="hidden" id={`cert-logo-replace-${index}`}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file && file.type.startsWith('image/')) {
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    updateItem('certifications', index, 'certification_logo', reader.result);
                                  };
                                  reader.readAsDataURL(file);
                                }
                                e.target.value = '';
                              }} />
                            <label htmlFor={`cert-logo-replace-${index}`} className="absolute inset-0 cursor-pointer flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-30 transition-all" title="Click to replace logo">
                              <ImageIcon className="w-5 h-5 text-white opacity-0 hover:opacity-100" />
                            </label>
                          </>
                        ) : (
                          <>
                            <input type="file" accept="image/*" className="hidden" id={`cert-logo-upload-${index}`}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (file && file.type.startsWith('image/')) {
                                  try {
                                    const cloudFrontUrl = await resumeAPI.uploadImageToS3(file, 'avatar');
                                    updateItem('certifications', index, 'certification_logo', cloudFrontUrl);
                                  } catch (error) {
                                    console.error('Failed to upload certification logo:', error);
                                    alert('Failed to upload logo. Please try again.');
                                  }
                                }
                                e.target.value = '';
                              }} />
                            <label htmlFor={`cert-logo-upload-${index}`} className="cursor-pointer w-full h-full flex items-center justify-center hover:bg-gray-200 transition-colors" title="Click to upload certification logo">
                              <ImageIcon className="w-6 h-6 text-gray-400" />
                            </label>
                          </>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-600">Upload a logo or icon for this certification</p>
                        {cert.certification_logo && (
                          <button
                            type="button"
                            onClick={() => updateItem('certifications', index, 'certification_logo', '')}
                            className="mt-2 text-xs text-red-600 hover:text-red-800"
                          >
                            Remove logo
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Name - Required */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                <input
                  type="text"
                  value={cert.name || ''}
                  onChange={(e) => updateItem('certifications', index, 'name', e.target.value)}
                      className="input w-full border border-gray-300 rounded"
                      placeholder="e.g., Project Management Professional (PMP)"
                    />
                  </div>

                  {/* Issuing organization - Required */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Issuing organization <span className="text-red-500">*</span>
                    </label>
                <input
                  type="text"
                  value={cert.issuer || ''}
                  onChange={(e) => updateItem('certifications', index, 'issuer', e.target.value)}
                      className="input w-full border border-gray-300 rounded"
                      placeholder="e.g., Project Management Institute"
                    />
                  </div>

                  {/* Start date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start date
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <select
                          value={cert.start_month || ''}
                          onChange={(e) => updateItem('certifications', index, 'start_month', e.target.value)}
                          className="input w-full border border-gray-300 rounded"
                        >
                          <option value="">Month</option>
                          <option value="January">January</option>
                          <option value="February">February</option>
                          <option value="March">March</option>
                          <option value="April">April</option>
                          <option value="May">May</option>
                          <option value="June">June</option>
                          <option value="July">July</option>
                          <option value="August">August</option>
                          <option value="September">September</option>
                          <option value="October">October</option>
                          <option value="November">November</option>
                          <option value="December">December</option>
                        </select>
                      </div>
                      <div>
                        <select
                          value={cert.start_year || ''}
                          onChange={(e) => updateItem('certifications', index, 'start_year', e.target.value)}
                          className="input w-full border border-gray-300 rounded"
                        >
                          <option value="">Year</option>
                          {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i).map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Issue date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Issue date
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <select
                          value={cert.issue_month || ''}
                          onChange={(e) => updateItem('certifications', index, 'issue_month', e.target.value)}
                          className="input w-full border border-gray-300 rounded"
                        >
                          <option value="">Month</option>
                          <option value="January">January</option>
                          <option value="February">February</option>
                          <option value="March">March</option>
                          <option value="April">April</option>
                          <option value="May">May</option>
                          <option value="June">June</option>
                          <option value="July">July</option>
                          <option value="August">August</option>
                          <option value="September">September</option>
                          <option value="October">October</option>
                          <option value="November">November</option>
                          <option value="December">December</option>
                        </select>
                      </div>
                      <div>
                        <select
                          value={cert.issue_year || ''}
                          onChange={(e) => updateItem('certifications', index, 'issue_year', e.target.value)}
                          className="input w-full border border-gray-300 rounded"
                        >
                          <option value="">Year</option>
                          {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i).map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Expiration date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiration date
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <select
                          value={cert.expiration_month || ''}
                          onChange={(e) => updateItem('certifications', index, 'expiration_month', e.target.value)}
                          className="input w-full border border-gray-300 rounded"
                        >
                          <option value="">Month</option>
                          <option value="January">January</option>
                          <option value="February">February</option>
                          <option value="March">March</option>
                          <option value="April">April</option>
                          <option value="May">May</option>
                          <option value="June">June</option>
                          <option value="July">July</option>
                          <option value="August">August</option>
                          <option value="September">September</option>
                          <option value="October">October</option>
                          <option value="November">November</option>
                          <option value="December">December</option>
                        </select>
                      </div>
                      <div>
                        <select
                          value={cert.expiration_year || ''}
                          onChange={(e) => updateItem('certifications', index, 'expiration_year', e.target.value)}
                          className="input w-full border border-gray-300 rounded"
                        >
                          <option value="">Year</option>
                          {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() + i).map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Credential ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Credential ID
                    </label>
                <input
                  type="text"
                      value={cert.credential_id || ''}
                      onChange={(e) => updateItem('certifications', index, 'credential_id', e.target.value)}
                      className="input w-full border border-gray-300 rounded"
                      placeholder="e.g., ABC123-XYZ789"
                />
              </div>

                  {/* Credential URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Credential URL
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="url"
                        value={cert.credential_url || ''}
                        onChange={(e) => updateItem('certifications', index, 'credential_url', e.target.value)}
                        className="input w-full border border-gray-300 rounded"
                        placeholder="https://www.credly.com/badges/..."
                      />
                      {cert.credential_url && (
                        <a
                          href={cert.credential_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary whitespace-nowrap"
                        >
                          Show credential
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Skills Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Skills
                    </label>
                    <p className="text-xs text-gray-600 mb-2">
                      Associate at least 1 skill to this license or certification. It'll also appear in your Skills section.
                    </p>
                    <div className="space-y-2">
                      {(cert.skills || []).map((skill, skillIndex) => (
                        <div key={skillIndex} className="flex items-center gap-2">
                          <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                const newSkills = (cert.skills || []).filter((_, i) => i !== skillIndex);
                                updateItem('certifications', index, 'skills', newSkills);
                              }}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              <X className="w-3 h-3" />
                            </button>
                            {skill}
                          </span>
            </div>
          ))}
                      <button
                        type="button"
                        onClick={() => {
                          const skillName = prompt('Enter skill name:');
                          if (skillName && skillName.trim()) {
                            const newSkills = [...(cert.skills || []), skillName.trim()];
                            updateItem('certifications', index, 'skills', newSkills);
                          }
                        }}
                        className="btn btn-outline text-sm inline-flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add skill
                      </button>
                    </div>
                  </div>
                </div>
                )}
              </div>
            );
          })}
          <button
            onClick={() => addItem('certifications')}
            className="btn btn-outline w-full inline-flex items-center justify-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Add License or Certification</span>
          </button>
        </div>
      </Section>

      {/* Languages */}
      <Section title="Languages" name="languages" expanded={expandedSections.languages} onToggle={toggleSection} hasData={sectionHasData.languages}>
        <div className="space-y-4">
          {(resume.languages || []).map((lang, index) => {
            const isCollapsed = collapsedLanguages[index] === true;
            const hasData = hasLanguageData(lang);
            
            return (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                  <div className="flex-1 flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-gray-900">Language {index + 1}</h4>
                    {hasData && (
                      <button
                        onClick={() => toggleLanguageCollapse(index)}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                        type="button"
                      >
                        {isCollapsed ? 'See more' : 'See less'}
                      </button>
                    )}
                    {isCollapsed && hasData && (
                      <div className="w-full mt-2 text-sm text-gray-600">
                        {lang.name && <span className="font-medium">{lang.name}</span>}
                        {lang.proficiency && <span className="ml-2 text-gray-500">({lang.proficiency})</span>}
                      </div>
                    )}
                  </div>
                <button
                  onClick={() => removeItem('languages', index)}
                    className="text-red-600 hover:text-red-800 flex-shrink-0"
                    type="button"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
                {!isCollapsed && (
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={lang.name || ''}
                  onChange={(e) => updateItem('languages', index, 'name', e.target.value)}
                  className="input"
                  placeholder="Language"
                />
                <select
                  value={lang.proficiency || 'Native'}
                  onChange={(e) => updateItem('languages', index, 'proficiency', e.target.value)}
                  className="select"
                >
                  <option value="Native">Native</option>
                  <option value="Fluent">Fluent</option>
                  <option value="Professional">Professional</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Basic">Basic</option>
                </select>
              </div>
                )}
            </div>
            );
          })}
          <button
            onClick={() => addItem('languages')}
            className="btn btn-outline w-full inline-flex items-center justify-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Add Language</span>
          </button>
        </div>
      </Section>
    </div>
  );
};

export default ResumeEditor;
